import { memo, useCallback, useMemo, useState } from 'react';

import Table from '@components/common/Table/Table';

import { useQueryResultingAssetAccessTypes } from '@hooks/assets/assetAccess/useQueryResultingAssetAccessTypes';
import { useMutateDisableAssetVisibility } from '@hooks/assets/assetVisibility/useMutateDisableAssetVisibility';
import { useMutateEnableAssetVisibility } from '@hooks/assets/assetVisibility/useMutateEnableAssetVisibility';
import { useQueryAssetVisibilityRestricted } from '@hooks/assets/assetVisibility/useQueryAssetVisibilityRestricted';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { defaultMeta } from '@enums/common';

import { booleanOptions, MIN_FILTER_WIDTH } from '@constants/common';
import { TableFilterTypes } from '@constants/table';

import { getColumns } from './utils';

import styles from '../../OrganisationProfile.module.scss';

interface IVisibilityMNOTableProps {
	id: string;
}

const VisibilityMNOTable = ({ id }: IVisibilityMNOTableProps) => {
	const {
		accessPermission: {
			platformOwnerAdministrator,
			assetOwnerOrganizationAdministrator,
		},
	} = useAppSelector(auth);

	const isToggleAccessAvailable = platformOwnerAdministrator || assetOwnerOrganizationAdministrator;

	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [searchValue, setSearchValue] = useState('');
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);

	const [selectedAccessStatus, setSelectedAccessStatus] = useState<TSelectOption<string> | null>(null);
	const [selectedAssetVisibilityType, setSelectedAssetVisibilityType] = useState<TSelectOption<string> | null>(null);

	const {
		assetVisibilityRestricted,
		countOfRecords,
		isLoadingAssetVisibilityRestricted,
	} = useQueryAssetVisibilityRestricted({
		id,
		page,
		pageSize,
		searchValue,
		sortDirection,
		sortFieldName,
		accessStatus: selectedAccessStatus?.value,
		resultingAccess: selectedAssetVisibilityType?.value,
	});

	const {
		assetAccessResultingTypes,
		isLoadingAssetAccessResultingTypes,
	} = useQueryResultingAssetAccessTypes();

	const {
		enableAssetVisibility,
		isLoadingEnableAssetVisibility,
	} = useMutateEnableAssetVisibility();

	const {
		disableAssetVisibility,
		isLoadingDisableAssetVisibility,
	} = useMutateDisableAssetVisibility();

	const handleAssetAccessChange = useCallback((value: TSelectOption<string>) => {
		setSelectedAccessStatus(value);
		setPage(defaultMeta.page);
	}, []);

	const handleAssetVisibilityChange = useCallback((value: TSelectOption<string>) => {
		setSelectedAssetVisibilityType(value);
		setPage(defaultMeta.page);
	}, []);

	const toggleAccess = useCallback((organizationId: string, isEnabled: boolean) => {
		if (!isToggleAccessAvailable) {
			return;
		}

		if (isEnabled) {
			disableAssetVisibility({ id, organizationId });
		} else {
			enableAssetVisibility({ id, organizationId });
		}
	}, [
		id,
		isToggleAccessAvailable,
		enableAssetVisibility,
		disableAssetVisibility,
	]);

	const filters: TTableFilter[] = useMemo(() => {
		return [
			{
				name: 'accessStatus',
				placeholder: 'Access status',
				option: selectedAccessStatus,
				options: booleanOptions,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleAssetAccessChange,
				defaultValue: null,
			},
			{
				name: 'visibilityType',
				placeholder: 'Reference template',
				option: selectedAssetVisibilityType,
				options: assetAccessResultingTypes,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleAssetVisibilityChange,
				defaultValue: null,
			},
		];
	}, [
		selectedAssetVisibilityType,
		assetAccessResultingTypes,
		selectedAccessStatus,
		handleAssetVisibilityChange,
		handleAssetAccessChange,
	]);

	const columns = useMemo(() => {
		return getColumns({ toggleAccess, isToggleAccessAvailable });
	}, [toggleAccess, isToggleAccessAvailable]);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
		setPage(defaultMeta.page);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
	]);

	const search: ITableSearch[] = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: 'Search by name',
	}], [searchValue, searchHandler]);

	const isLoading = isLoadingAssetVisibilityRestricted
		|| isLoadingAssetAccessResultingTypes
		|| isLoadingEnableAssetVisibility
		|| isLoadingDisableAssetVisibility;

	return (
		<div className={styles.tableWrapper}>
			<Table
				columns={columns}
				data={assetVisibilityRestricted}
				filters={filters}
				isLoading={isLoading}
				isWithoutCheckbox
				isWithoutDeleteAction
				pagination={pagination}
				search={search}
				sort={sort}
			/>
		</div>
	);
};

export default memo(VisibilityMNOTable);
